/**
 * Axios client for OUR Casanéo backend (FastAPI hosted with this SPA).
 *
 * Auth uses BOTH:
 *  - httpOnly cookies (`cn_access_token` / `cn_refresh_token`) — primary,
 *    sent thanks to `withCredentials: true`.
 *  - `Authorization: Bearer <access_token>` header — fallback, stored in
 *    localStorage. This is critical when the browser drops or refuses to
 *    send the SameSite=None cookies (Safari ITP, Chrome partitioning,
 *    tab isolation, service worker races…). The Casanéo backend accepts
 *    both mechanisms in `get_current_user` (see server.py).
 *
 * Anything owned by Casanéo (WhatsApp, /auth/*, future server-side
 * trash, tenant settings) goes through `localApi`. Legacy calls remain on
 * `src/lib/api.js` (external `rental-hub-manager.emergent.host`).
 */
import axios from "axios";
import { toast } from "sonner";

const BASE = process.env.REACT_APP_BACKEND_URL;
export const CN_TOKEN_KEY = "cn_bearer_token";
export const CN_REFRESH_KEY = "cn_refresh_token_fallback";

export const getCnRefresh = () => {
  try { return localStorage.getItem(CN_REFRESH_KEY) || null; } catch { return null; }
};
export const setCnRefresh = (t) => {
  try {
    if (t) localStorage.setItem(CN_REFRESH_KEY, t);
    else localStorage.removeItem(CN_REFRESH_KEY);
  } catch { /* noop */ }
};

export const getCnToken = () => {
  try { return localStorage.getItem(CN_TOKEN_KEY) || null; } catch { return null; }
};
export const setCnToken = (t) => {
  try {
    if (t) localStorage.setItem(CN_TOKEN_KEY, t);
    else localStorage.removeItem(CN_TOKEN_KEY);
  } catch { /* noop */ }
};
export const clearCnToken = () => setCnToken(null);

export const localApi = axios.create({
  baseURL: `${BASE}/api`,
  timeout: 45000,
  // ⚠️ `withCredentials` volontairement désactivé — l'ingress Kubernetes
  // renvoie `Access-Control-Allow-Origin: *` en même temps que
  // `Access-Control-Allow-Credentials: true`, combinaison invalide selon
  // la spec CORS que le navigateur bloque intégralement. Résultat quand
  // le frontend est servi sur un autre domaine que l'API (ex : prod
  // `www.casaneo.pro` → API `preview.emergentagent.com`) : toutes les
  // requêtes échouaient silencieusement (impayés vides, tenant-settings
  // qui « ne se maintiennent pas », correction montants pas sauvée…).
  // On se repose entièrement sur `Authorization: Bearer` — refresh_token
  // passe déjà dans le body (voir intercepteur ci-dessous).
  withCredentials: false,
});

// Attach the Bearer token if we captured one from /auth/login or
// /auth/refresh. Cookies remain primary — this is redundancy, not a
// replacement.
localApi.interceptors.request.use((config) => {
  const t = getCnToken();
  if (t && !config.headers?.Authorization) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${t}`;
  } else if (!t && !config.headers?.Authorization) {
    // Compte legacy uniquement (ex. propriétaire invité) : le backend Casanéo
    // sait résoudre un jeton `csk_…` via legacy /auth/me → périmètre propriétaire.
    let legacy = null;
    try { legacy = localStorage.getItem("casaneo_token"); } catch { /* noop */ }
    if (legacy) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${legacy}`;
    }
  }
  return config;
});

// Attempt a silent refresh on 401 exactly once per response to keep the
// user's session alive across page reloads without disturbing the UI.
let refreshPromise = null;
let sessionToastShown = false;
localApi.interceptors.response.use(
  (r) => {
    // Capture the fresh access_token on every login / refresh response so
    // future requests can present it via Authorization header when the
    // cookies get dropped.
    try {
      const url = r?.config?.url || "";
      const tok = r?.data?.access_token;
      if (tok && (url.includes("/auth/login") || url.includes("/auth/refresh") || url.includes("/auth/google/session") || url.includes("/auth/register"))) {
        setCnToken(tok);
        // Same fallback for the refresh token: without it the session dies
        // after 15 min whenever the browser drops our httpOnly cookies.
        if (r?.data?.refresh_token) setCnRefresh(r.data.refresh_token);
      }
      if (url.includes("/auth/logout")) {
        clearCnToken();
        setCnRefresh(null);
      }
    } catch { /* noop */ }
    return r;
  },
  async (error) => {
    const original = error?.config || {};
    const status = error?.response?.status;
    const url = original.url || "";
    if (
      status !== 401 ||
      original.__isRetry ||
      url.includes("/auth/login") ||
      url.includes("/auth/refresh") ||
      url.includes("/auth/register")
    ) {
      return Promise.reject(error);
    }
    original.__isRetry = true;
    try {
      if (!refreshPromise) {
        refreshPromise = localApi
          .post("/auth/refresh", { refresh_token: getCnRefresh() || undefined })
          .finally(() => { refreshPromise = null; });
      }
      await refreshPromise;
      // Refresh may have rotated the Bearer token — drop the stale one
      // from the retried request so the request interceptor injects the
      // fresh value.
      if (original.headers) delete original.headers.Authorization;
      return localApi(original);
    } catch (e) {
      // Both access and refresh are gone / expired. The user must
      // re-login to open a fresh Casanéo session.
      clearCnToken();
      setCnRefresh(null);
      // Skip the toast when:
      //  - the request was a bootstrap read (/auth/me) — happens before
      //    the user has any credentials, doesn't warrant a UI message.
      //  - the app is currently on /login (no session by design).
      //  - the request was a non-mutating GET (only mutations lose data).
      const isAuthMe = url.includes("/auth/me");
      const isOnLoginPage = typeof window !== "undefined" && window.location.pathname === "/login";
      const isMutation = ["post", "put", "patch", "delete"].includes(String(original.method || "get").toLowerCase());
      if (!isAuthMe && !isOnLoginPage && isMutation && !sessionToastShown) {
        sessionToastShown = true;
        toast.error("Session Casanéo expirée — reconnectez-vous pour éditer les montants", {
          action: {
            label: "Se reconnecter",
            onClick: () => {
              try { localStorage.removeItem(CN_TOKEN_KEY); } catch { /* noop */ }
              // `/login` is the LEGACY door and bounces back to `/` when the
              // legacy session is still valid — the Casanéo session lives on
              // `/casaneo-login`. `next` brings the user back where he was.
              const next = window.location.pathname + window.location.search;
              window.location.href = `/casaneo-login?next=${encodeURIComponent(next)}`;
            },
          },
          duration: 10000,
        });
        setTimeout(() => { sessionToastShown = false; }, 10000);
      }
      return Promise.reject(error);
    }
  }
);
